import {
  BankOutlined,
  FolderOutlined,
  LineOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import TreeItem from "@mui/lab/TreeItem";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "src/store";
import {
  nodeRecordsSelectors,
  selectChildrenIdsByParentId,
} from "../node-tree-records.slice";
import { NodeRecord, NodeType } from "../node-tree.defenitions";
import TreeItemActions from "./TreeItemActions";
import TreeItemLabel from "./TreeItemLabel";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useMemo } from "react";

const WrappedTreeNode = React.memo(({ nodeId }: WrappedTreeNodeProps) => {
  const { formatMessage } = useIntl();

  const childrenIds = useSelector(
    (state: RootState) => selectChildrenIdsByParentId(state, nodeId),
    shallowEqual
  );

  const isSelected = useSelector(
    (state: RootState) => state.nodeRecords.selectedNodeId === nodeId
  );

  const node = useSelector(
    (state: RootState) =>
      nodeRecordsSelectors.selectById(state.nodeRecords, nodeId) as NodeRecord
  );

  const icon = useMemo(() => nodeIcon(node.type), [node.type]);

  const label = useMemo(
    () => (
      <TreeItemLabel
        name={
          node.type === NodeType.GRADE
            ? formatMessage({
                id: node.name
                  ? `grades.${node.name}`
                  : "nodes.newNames.grade",
              })
            : node.name
        }
        actions={
          isSelected && (
            <TreeItemActions
              nodeId={nodeId}
              nodeType={node.type}
              nodeKey={node.key}
            />
          )
        }
      />
    ),
    [node.name, node.type, node.key, nodeId, isSelected, formatMessage]
  );

  return (
    <TreeItem
      nodeId={`${node.id}`}
      collapseIcon={icon}
      expandIcon={icon}
      label={label}
    >
      {childrenIds.map((childId) => (
        <WrappedTreeNode key={childId} nodeId={childId} />
      ))}
      {childrenIds.length === 0 && node.type !== NodeType.CLASS && (
        <TreeItem
          icon={<LineOutlined />}
          nodeId="empty"
          disabled
          label={<FormattedMessage id="nodes.node.empty" />}
        />
      )}
    </TreeItem>
  );
});

export default WrappedTreeNode;

interface WrappedTreeNodeProps {
  nodeId: number;
}

export const nodeIcon = (nodeType: NodeType) => {
  switch (nodeType) {
    case NodeType.GROUP:
      return <FolderOutlined />;
    case NodeType.CLASS:
      return <TeamOutlined />;
    case NodeType.SCHOOL:
      return <BankOutlined />;
    default:
      return undefined;
  }
};
